import { useEffect, useRef, useState } from "react";
import client from "./api";

// Session cache: "lang|text" -> mp3 data URI (audio is also cached server-side in Mongo).
const cache = new Map();
const inflight = new Map();

// Client-side concurrency limiter. TTS synthesis is expensive and the shared LLM key
// rate-limits, so we never fire more than a couple of requests at once. This prevents the
// "warm every clip at once" storm that made uncached (non-English) tours 502 and go silent.
const MAX_CONCURRENT = 2;
let activeCount = 0;
const queue = [];
function pump() {
  if (activeCount >= MAX_CONCURRENT) return;
  const job = queue.shift();
  if (!job) return;
  activeCount += 1;
  job.run().then(job.resolve, job.reject).finally(() => { activeCount -= 1; pump(); });
}
function schedule(run, priority = false) {
  return new Promise((resolve, reject) => {
    const job = { run, resolve, reject };
    if (priority) queue.unshift(job); else queue.push(job);
    pump();
  });
}

export function fetchNarration(text, lang = "ENG", priority = false) {
  const key = `${lang}|${text}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);
  const p = schedule(() => client.post("/site/narrate", { text, lang }), priority)
    .then(({ data }) => { cache.set(key, data.audio); inflight.delete(key); return data.audio; })
    .catch((e) => { inflight.delete(key); throw e; });
  inflight.set(key, p);
  return p;
}

// Drives synced voice-over for a walkthrough. When `voiceOn` && `playing`, it fetches + plays the
// current step's clip and calls `onEnded` when the clip finishes (so the tour advances in sync).
// `prefetch` is a list of upcoming clip texts warmed in the background so transitions are gapless.
// Falls back to a timer if TTS fails or the browser blocks playback.
export function useNarrator({ text, playing, voiceOn, lang = "ENG", onEnded, stepKey, prefetch = [] }) {
  const audioRef = useRef(null);
  const endedRef = useRef(onEnded);
  endedRef.current = onEnded;
  const prefetchRef = useRef(prefetch);
  prefetchRef.current = prefetch;
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  if (!audioRef.current && typeof Audio !== "undefined") audioRef.current = new Audio();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    let cancelled = false;
    let fallback = null;
    let safety = null;
    let breath = null;
    const finish = () => {
      // small natural pause after a clip ends before advancing to the next section
      if (breath) clearTimeout(breath);
      breath = setTimeout(() => { if (!cancelled && endedRef.current) endedRef.current(); }, 350);
    };
    const cleanup = () => {
      cancelled = true;
      [fallback, safety, breath].forEach((t) => t && clearTimeout(t));
      audio.onended = null;
      setSpeaking(false);
      try { audio.pause(); } catch (e) { /* noop */ }
    };
    if (!playing || !voiceOn || !text) return cleanup;
    setLoading(true);
    // warm upcoming clips in the background so there's no gap between sections (throttled queue)
    (prefetchRef.current || []).forEach((t) => { if (t) fetchNarration(t, lang).catch(() => {}); });
    fetchNarration(text, lang, true).then((uri) => {
      if (cancelled) return;
      setLoading(false);
      audio.src = uri;
      try { audio.currentTime = 0; } catch (e) { /* noop */ }
      audio.onended = () => { if (cancelled) return; setSpeaking(false); finish(); };
      const p = audio.play();
      if (p && p.then) p.then(() => { if (!cancelled) setSpeaking(true); }).catch(() => { if (!cancelled) fallback = setTimeout(finish, 6000); });
      // hard safety scaled to the clip length (advances even if 'ended' never fires)
      const onMeta = () => { if (safety) clearTimeout(safety); const ms = (audio.duration && isFinite(audio.duration) ? audio.duration * 1000 : 12000) + 4000; safety = setTimeout(finish, ms); };
      if (audio.readyState >= 1 && audio.duration) onMeta(); else audio.addEventListener("loadedmetadata", onMeta, { once: true });
    }).catch(() => {
      if (cancelled) return;
      setLoading(false);
      fallback = setTimeout(finish, 6000);
    });
    return cleanup;
  }, [text, playing, voiceOn, lang, stepKey]);

  return { loading, speaking };
}
